// CRUD Clientes
//

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import PrivatePageSection from '../../components/layout/PrivatePageSection'
import {
  Alert,
  Box,
  Chip,
  Container,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Stack,
  Avatar,
  Paper,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

const pick = (obj, snakeKey, camelKey, fallback = '') => {
  if (!obj) return fallback
  const snakeVal = obj[snakeKey]
  if (
    snakeVal !== undefined &&
    snakeVal !== null &&
    String(snakeVal).trim() !== ''
  )
    return snakeVal

  const camelVal = obj[camelKey]
  if (
    camelVal !== undefined &&
    camelVal !== null &&
    String(camelVal).trim() !== ''
  )
    return camelVal

  return fallback
}

const Clients = () => {
  const navigate = useNavigate()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await api.get('/clients')
        setClients(Array.isArray(response.data) ? response.data : [])
      } catch (error) {
        setError('No fue posible cargar los clientes.')
        setClients([])
      } finally {
        setLoading(false)
      }
    }

    fetchClients()
  }, [])

  if (loading) {
    return (
      <Container sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
      </Container>
    )
  }

  if (error) return <Alert severity="error">{error}</Alert>

  return (
    <PrivatePageSection
      title="Clientes"
      subtitle="Catálogo operativo de clientes. Selecciona una tarjeta para abrir la ficha de detalle."
    >
      <Grid container spacing={{ xs: 2, md: 2.5 }}>
        {clients.length > 0 ? (
          clients.map((client) => {
            const displayName = pick(
              client,
              'full_name_or_company',
              'fullNameOrCompany',
              '(Sin nombre)'
            )

            const sector = pick(
              client,
              'sector_or_industry',
              'sectorOrIndustry',
              ''
            )

            const phone = pick(client, 'num_phone', 'numPhone', '')
            const email = pick(client, 'email', 'email', '')
            const logo = pick(client, 'logo', 'logo', '')
            const clientId = pick(client, 'id', 'id', null)
            const productsCountRaw = pick(
              client,
              'products_count',
              'productsCount',
              null
            )
            const parsedProductsCount = Number(productsCountRaw)
            const hasProductsCount = Number.isFinite(parsedProductsCount)
            const productsCount = hasProductsCount ? parsedProductsCount : null
            const hasValidId =
              clientId !== null &&
              clientId !== undefined &&
              String(clientId).trim() !== ''

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={client.id || displayName}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderRadius: 2.5,
                    transition:
                      'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                    '&:hover': hasValidId
                      ? {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                          borderColor: 'primary.main',
                        }
                      : undefined,
                  }}
                >
                  <CardActionArea
                    disabled={!hasValidId}
                    onClick={() =>
                      navigate(`/clients/${clientId}`, { state: { client } })
                    }
                    sx={{ height: '100%' }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        minHeight: 210,
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={logo}
                          alt={displayName}
                          sx={{ width: 56, height: 56 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, lineHeight: 1.25 }}
                          >
                            {displayName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.35 }}
                          >
                            ID: {clientId || '—'}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        sx={{ flexWrap: 'wrap' }}
                      >
                        <Chip
                          size="small"
                          color={sector ? 'primary' : 'default'}
                          variant={sector ? 'filled' : 'outlined'}
                          label={sector || 'Sector no especificado'}
                        />
                        {hasProductsCount ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Productos: ${productsCount}`}
                          />
                        ) : null}
                      </Stack>

                      <Divider />

                      <Stack spacing={0.5}>
                        <Typography variant="body2">Email: {email || '-'}</Typography>
                        <Typography variant="body2">
                          Teléfono: {phone || '-'}
                        </Typography>
                      </Stack>

                      <Box sx={{ mt: 'auto', pt: 0.5 }}>
                        <Typography
                          variant="body2"
                          color={hasValidId ? 'primary.main' : 'text.disabled'}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            fontWeight: 600,
                          }}
                        >
                          {hasValidId
                            ? 'Abrir ficha de cliente'
                            : 'Detalle no disponible'}
                          <ArrowForwardIcon fontSize="inherit" />
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            )
          })
        ) : (
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 2.5,
                backgroundColor: 'background.paper',
              }}
            >
              <Stack spacing={1}>
                <Alert severity="info">
                  No hay clientes registrados en este momento.
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  Cuando existan clientes activos, aparecerán aquí como catálogo
                  visual para navegar a su ficha de detalle.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        )}
      </Grid>
    </PrivatePageSection>
  )
}

export default Clients
